import { Http } from '@capacitor-community/http'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { PDFsRows } from '../types/database'
import { useDatabase } from '../composables/database'
import { WriteFileOptions } from '@capacitor/filesystem'
import { useFilesystem } from '../composables/filesystem'
import { useDevicePDFsStore } from '../store/useDevicePDFsStore'

type PDFsModelReturn = {
  writePDF: (data: { filePath: string; url: string }) => Promise<void>
  updateDateOpened: (data: { filePath: string }) => Promise<void>
  downloadPDF: (data: { filePath: string; url: string }) => Promise<void>
  /** removes list of pdfs from device.
   * @param data: string[]. Array of file paths (pdf names)
   */
  removePDFs: (data: string[]) => Promise<void>
  /** removes pdfs after 30 days */
  removeStalePDFs: () => Promise<void>
  getSystemPDFs: () => Promise<string[]>
  openPDF: (src: WriteFileOptions['path']) => void
  clearPDFs: () => Promise<void>
}

const pdfFolder = 'FHS/test_pdfs'
/**
 * Interacts with pdf table and device pdf folder (mobile only)
 * @returns {PDFsModelReturn}
 */
export const PDFsModel = (): PDFsModelReturn => {
  const { getURI } = useFilesystem()
  const { run, query } = useDatabase()
  const { addToPDFsStore, removeFromPDFsStore } = useDevicePDFsStore()

  //Path = document name
  const downloadPDF = async (data: { filePath: string; url: string }) => {
    if (data.url.length < 1) return void 0
    //mkdir
    await makePDFsDir()

    console.log('downloading file', {
      filePath: `${pdfFolder}/${generateFileNameFromURL(data.filePath)}`,
    })
    await Http.downloadFile({
      method: 'GET',
      filePath: `${pdfFolder}/${generateFileNameFromURL(data.filePath)}`,
      url: data.url,
      fileDirectory: Directory.Data,
    }).catch((e) => {
      console.error(`Error downloading PDF ${data.filePath}: ${e}`)
      console.error(e)
    })
  }

  //Capacitor only
  const writePDF = async (data: { filePath: string; url: string }): Promise<void> => {
    if (data.url.length < 1) return void 0
    const sql = `
    INSERT INTO pdfs ('date', 'pdf_path', 'pdf_url')
    VALUES (date(), ?, ?);
    `
    await run(sql, [generateFileNameFromURL(data.filePath), data.url])
      .catch((error) => console.log(`Error writing PDF: ${error}`))
      .then(() => {
        addToPDFsStore([generateFileNameFromURL(data.filePath)])
      })
  }

  //Capacitor only
  const updateDateOpened = async (data: { filePath: string }): Promise<void> => {
    const sql = `
        REPLACE INTO pdfs ('id', 'task_id', 'date', 'date_opened', 'pdf_url',)
        VALUES (
          (SELECT id FROM pdfs WHERE pdf_url = ?),
          (SELECT task_id FROM pdfs WHERE pdf_url = ?),
          (SELECT date FROM pdfs WHERE pdf_url = ?),
          date(),
          ?
        );
    `

    const fileName = generateFileNameFromURL(data.filePath)

    await run(sql, [fileName, fileName, fileName, fileName]).catch((error) =>
      console.log(`Error updating date_opened from pdfs: ${error}`)
    )
  }

  //Remove pdf over 30 days since last opened
  const removeStalePDFs = async () => {
    const sql = `
        SELECT * FROM pdfs WHERE 'date_opened' <= date('now','-30 day');
    `
    await query(sql)
      .catch((error) => console.log(`Error querying stale data: ${error}`))
      .then((stalePDFs: PDFsRows | void | null) => {
        if (stalePDFs) {
          stalePDFs.forEach(async (row) => {
            await Filesystem.deleteFile({
              path: `${pdfFolder}/${row.pdf_path}`,
              directory: Directory.Data,
            })
              .catch((error) => {
                console.log(`Error removing file: ${error}`)
              })
              .then(() => {
                run("DELETE FROM pdfs WHERE 'date' <= date('now', '-30 day'); ", []).catch(
                  (error) => {
                    console.log(`Error removing stale data: ${error}`)
                  }
                )
              })
          })
        }
      })
  }
  //Capacitor only
  const removePDFs = async (data: string[]) => {
    //Make list sqlite readable
    const queryData = data.join(',')

    const sql = `
        DELETE FROM pdfs WHERE 'pdf_path' NOT IN (?);
    `
    await run(sql, [queryData])
      .catch((error) => {
        console.log(`Error syncing PDF table: ${error}`)
      })
      .then(() => {
        data.forEach(async (item) => {
          removeFromPDFsStore([generateFileNameFromURL(item)])
          await Filesystem.deleteFile({
            path: `${pdfFolder}/${generateFileNameFromURL(item)}`,
            directory: Directory.Data,
          })
        })
      })
  }

  //Capacitor only
  const makePDFsDir = async () => {
    await Filesystem.mkdir({
      path: pdfFolder,
      directory: Directory.Data,
      recursive: true,
    }).catch((error) => {
      if (error != 'Error: Directory exists') {
        console.log(`Error creating directory: ${error}`)
      } else {
        console.error('Error making pdf dir')
        console.error(error)
      }
    })
  }
  //Capacitor only
  const getSystemPDFs = async (): Promise<string[]> => {
    //mkdir
    void makePDFsDir()
    //get PDF names
    const devicePDFs = await Filesystem.readdir({
      directory: Directory.Data,
      path: pdfFolder,
    }).catch(async (error) => {
      console.log({ error })
    })
    if (devicePDFs) return devicePDFs.files
    else return []
  }

  //Capacitor only
  const openPDF = async (src: WriteFileOptions['path']) => {
    await getURI({ path: `/${pdfFolder}/${generateFileNameFromURL(src)}` })
      .then(async (result) => {
        //@ts-expect-error cordova plugins loaded when app init
        await cordova.plugins.fileOpener2.open(result, 'application/pdf', {
          error: function (e: any) {
            const name = generateFileNameFromURL(src)
            console.error(`Error opening PDF ${name}: ${e}`)
            console.error(e)
          },
          success: function () {
            console.log('file opened successfully')
          },
        })
        // await updateDateOpened({ filePath: generateFileNameFromURL(src) }).catch((error) => console.log(error))
      })
      .catch((e) => {
        const name = generateFileNameFromURL(src)
        console.error(`Error opening PDF ${name}: ${e}`)
        console.error(e)
      })
  }

  const clearPDFs = async () => {
    const sql = 'DELETE FROM pdfs'
    await run(sql, []).catch((error) => console.log(`Error deleting PDFs: ${error}`))
  }
  return {
    writePDF,
    updateDateOpened,
    downloadPDF,
    removePDFs,
    removeStalePDFs,
    getSystemPDFs,
    openPDF,
    clearPDFs,
  }
}

export const generateFileNameFromURL = (url: string) => {
  if (url) {
    return url.split('/')[url.split('/').length - 1]
  } else return ''
}
